import React from 'react';
import {
  FileText,
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  CalendarClock,
  Loader2,
  Mic,
  Pencil,
} from 'lucide-react';
import { RefreshButton } from '@/components/ui/refresh-button';
import { getAvatarColors } from '@/lib/utils/avatar';
import { PATIENTS_TABLE_GRID } from '@/components/shared/AppointmentsTable';

export interface RecordsViewProps {
  header: { title: string; subtitle?: string };
  search: { value: string; onChange: (v: string) => void; placeholder?: string };
  data: {
    rows: Array<{
      uuid: string;
      identifier: string;
      name: string;
      age: number;
      gender: 'M' | 'F' | 'O' | 'U';
      phoneNumber?: string | null;
      status: 'active' | 'inactive' | 'deceased';
      lastUpdated?: string | null;
    }>;
  };
  isLoading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onRefresh?: () => void;
  actions: {
    onCreatePatient?: () => void;
    onRowClick: (uuid: string) => void;
    onClearFilters: () => void;
    onStartConsultation?: (patientUuid: string) => void;
    onScheduleAppointment?: (patient: {
      uuid: string;
      name: string;
      age: number;
      gender: 'M' | 'F' | 'O' | 'U';
      phoneNumber?: string | null;
    }) => void;
    onEditPatient?: (patientUuid: string) => void;
  };
  isStartingConsultation?: string | null; // Track which patient is being opened
  /** Show the consultation (mic) button - for roles that can start consultations */
  showConsultationButton?: boolean;
  /** Show the edit patient button - only for administrators */
  showEditPatientButton?: boolean;
  /** Tab control for role-based patient filtering */
  tabs?: {
    activeTab: 'my-patients' | 'all-patients';
    onTabChange: (tab: 'my-patients' | 'all-patients') => void;
  };
  /** Doctor filter for administrators */
  doctorFilter?: {
    value: string | undefined;
    onChange: (value: string | undefined) => void;
    options: Array<{ id: string; name: string }>;
  };
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    hasPrev: boolean;
    hasNext: boolean;
    onPrev: () => void;
    onNext: () => void;
  };
}

// Helper function to format gender display with slash (e.g., "Male / 25")
const formatGenderWithAge = (gender: string, age: number) => {
  const genderMap: Record<string, string> = {
    M: 'Male',
    F: 'Female',
    O: 'Other',
    U: 'Unknown',
  };
  return `${age} ${genderMap[gender] || gender}`;
};

export const RecordsView: React.FC<RecordsViewProps> = ({
  header,
  search,
  data,
  isLoading,
  error,
  onRetry,
  onRefresh,
  actions,
  isStartingConsultation,
  showConsultationButton = true,
  showEditPatientButton = false,
  tabs,
  doctorFilter,
  pagination,
}) => {
  // Calculate total pages for pagination display
  const totalPages = Math.ceil(pagination.total / pagination.pageSize) || 1;

  return (
    <div className="relative flex w-full flex-col bg-[#e1e3ed]">
      <div className="flex flex-col py-6">
        {/* Header Section - Title, Search, Button */}
        <div className="flex items-center justify-between gap-4">
          {/* Title */}
          <h1 className="flex-shrink-0 font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))] tracking-normal text-gray-700">
            {header.title}
          </h1>

          {/* Tabs, Doctor Filter, Search, and Button - Right aligned */}
          <div className="flex items-center gap-3">
            {/* Tab Buttons */}
            {tabs && (
              <div className="flex gap-2">
                <button
                  onClick={() => tabs.onTabChange('my-patients')}
                  className={`rounded-full px-4 py-2 font-hanken text-[calc(14px*var(--app-scale))] font-medium transition-colors ${
                    tabs.activeTab === 'my-patients'
                      ? 'bg-[#061a61] text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  My Patients
                </button>
                <button
                  onClick={() => tabs.onTabChange('all-patients')}
                  className={`rounded-full px-4 py-2 font-hanken text-[calc(14px*var(--app-scale))] font-medium transition-colors ${
                    tabs.activeTab === 'all-patients'
                      ? 'bg-[#061a61] text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  All Patients
                </button>
              </div>
            )}

            {/* Doctor Filter Dropdown (administrator only, shown on All Patients tab) */}
            {tabs && doctorFilter && tabs.activeTab === 'all-patients' && (
              <select
                value={doctorFilter.value || 'all'}
                onChange={(e) =>
                  doctorFilter.onChange(e.target.value === 'all' ? undefined : e.target.value)
                }
                className="h-9 rounded-lg border border-gray-200 bg-white px-3 font-hanken text-[calc(14px*var(--app-scale))] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
              >
                <option value="all">All Doctors</option>
                {doctorFilter.options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            )}

            {/* Search Bar */}
            <div className="relative w-[calc(280px*var(--app-scale))]">
              <Search className="absolute left-3 top-1/2 h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] -translate-y-1/2 text-gray-700" />
              <input
                type="text"
                placeholder={search.placeholder || 'Search Patient'}
                value={search.value}
                onChange={(e) => search.onChange(e.target.value)}
                className="w-full rounded-full bg-white py-2 pl-10 pr-4 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 shadow-sm placeholder:text-[#bcc0d7] focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
              />
            </div>

            {/* Add Patient Button */}
            {actions.onCreatePatient && (
              <button
                onClick={actions.onCreatePatient}
                className="flex h-9 items-center gap-1 rounded-lg bg-[#061a61] px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-white hover:bg-[#0a2480]"
              >
                <Plus className="h-[calc(16px*var(--app-scale))] w-[calc(16px*var(--app-scale))]" />
                Add Patient
              </button>
            )}
          </div>
        </div>

        {/* Table Section */}
        <div className="mt-4 flex flex-col">
          <div className="relative flex flex-col overflow-x-auto rounded-3xl bg-white shadow-sm">
            {/* Refresh Button - positioned in top right corner of table */}
            {onRefresh && (
              <RefreshButton
                onClick={onRefresh}
                isLoading={isLoading}
                className="absolute right-4 top-3"
              />
            )}
            <div className="flex min-w-[960px] flex-col">
              {/* Table Header with inline filters */}
              <div
                className={`grid flex-shrink-0 ${PATIENTS_TABLE_GRID} items-center gap-[2px] border-b border-gray-200 px-4 py-3`}
              >
                <div className="text-left">
                  <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                    Patient name
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                    Age/Gender
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                    Contact
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                    Last Updated
                  </p>
                </div>
                <div className="text-center">
                  <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.018px] text-[#061A61]">
                    Action
                  </p>
                </div>
              </div>

              {/* Table Body */}
              {isLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-[calc(32px*var(--app-scale))] w-[calc(32px*var(--app-scale))] animate-spin rounded-full border-4 border-[#6b8af9] border-t-transparent" />
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <p className="font-hanken text-[calc(16px*var(--app-scale))] text-red-500">
                    Failed to load patients. Please try again.
                  </p>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      className="mt-4 rounded-md bg-[#6B8AF9] px-4 py-2 text-[calc(16px*var(--app-scale))] text-white hover:bg-[#566EC7]"
                    >
                      Retry
                    </button>
                  )}
                </div>
              ) : data.rows.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <p className="font-hanken text-[calc(16px*var(--app-scale))] text-gray-700">
                    {search.value
                      ? 'No patients found matching your search.'
                      : 'No patients found.'}
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {data.rows.map((patient) => (
                    <div
                      key={patient.uuid}
                      className={`grid ${PATIENTS_TABLE_GRID} items-center gap-[2px] px-4 py-2 hover:bg-gray-50`}
                    >
                      {/* Patient Info */}
                      <div className="flex items-center gap-3">
                        {(() => {
                          const colors = getAvatarColors(patient.name);
                          return (
                            <div
                              className="flex h-[calc(50px*var(--app-scale))] w-[calc(50px*var(--app-scale))] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/50"
                              style={{ backgroundColor: colors.bg }}
                            >
                              <span
                                className="font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))]"
                                style={{ color: colors.text }}
                              >
                                {patient.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          );
                        })()}
                        <div className="flex min-w-0 flex-col gap-0.5">
                          <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {patient.name}
                          </p>
                        </div>
                      </div>

                      {/* Age/Gender with slash */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {formatGenderWithAge(patient.gender, patient.age)}
                        </p>
                      </div>

                      {/* Contact */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {patient.phoneNumber || '-'}
                        </p>
                      </div>

                      {/* Last Updated */}
                      <div className="text-center">
                        <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                          {patient.lastUpdated || '-'}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-shrink-0 items-center justify-end gap-2">
                        {/* View Details - only for clinical roles (not front desk) */}
                        {showConsultationButton && (
                          <button
                            onClick={() => actions.onRowClick(patient.uuid)}
                            className="flex flex-shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-[#eaebf3] bg-[#eef6ff] px-2 hover:bg-[#dbeafe]"
                            style={{ height: 'calc(36px * var(--app-scale))' }}
                          >
                            <FileText className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] flex-shrink-0 text-[#566ec7]" />
                            <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] tracking-[-0.18px] text-[#6b8af9]">
                              View details
                            </span>
                          </button>
                        )}

                        {/* CalendarClock Button - Schedule Appointment */}
                        <button
                          onClick={() =>
                            actions.onScheduleAppointment?.({
                              uuid: patient.uuid,
                              name: patient.name,
                              age: patient.age,
                              gender: patient.gender,
                              phoneNumber: patient.phoneNumber,
                            })
                          }
                          className="flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]"
                          title="Schedule Appointment"
                        >
                          <CalendarClock className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#6b8af9]" />
                        </button>

                        {/* Edit Patient Button - Only for administrators */}
                        {showEditPatientButton && (
                          <button
                            onClick={() => actions.onEditPatient?.(patient.uuid)}
                            className="flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]"
                            title="Edit Patient"
                          >
                            <Pencil className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#6b8af9]" />
                          </button>
                        )}

                        {/* Mic Button - Start Consultation (only for roles that can start consultations) */}
                        {showConsultationButton && (
                          <button
                            onClick={() => actions.onStartConsultation?.(patient.uuid)}
                            disabled={isStartingConsultation === patient.uuid}
                            className={`flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border border-[#eaebf3] ${
                              isStartingConsultation === patient.uuid
                                ? 'cursor-not-allowed bg-gray-100 opacity-50'
                                : 'bg-[#eef6ff] hover:bg-[#dbeafe]'
                            }`}
                            title={
                              isStartingConsultation === patient.uuid
                                ? 'Opening...'
                                : 'Start Consultation'
                            }
                          >
                            {isStartingConsultation === patient.uuid ? (
                              <Loader2 className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] animate-spin text-[#6b8af9]" />
                            ) : (
                              <Mic className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#6b8af9]" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Pagination - Fixed at bottom right */}
        {!isLoading && pagination.total > 0 && (
          <div className="fixed bottom-4 right-6">
            <div className="flex items-center gap-4 rounded-full bg-[#e6e8ef] px-4 py-2 shadow-md">
              <button
                onClick={pagination.onPrev}
                disabled={!pagination.hasPrev}
                className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
              >
                <ChevronLeft className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
              </button>
              <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-[#42434b]">
                {pagination.page} out of {totalPages}
              </span>
              <button
                onClick={pagination.onNext}
                disabled={!pagination.hasNext}
                className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
              >
                <ChevronRight className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecordsView;
